/* eslint-disable no-lonely-if */
/* eslint-disable no-param-reassign */
import React from "react";
import dayjs from "dayjs";
import { makeObservable, observable, action } from "mobx";
import { Props as DataGridProps } from "../components/DataGrid/DataGrid";
import { getTemplates } from "../api/Templates";
import { UtcDateFormatter } from "../utils/dateformat/dateFormat";
import { Cookies } from "react-cookie";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PushPinIcon from "@mui/icons-material/PushPin";
import { defaultAllCode } from "../pages/Templates/LanguageCode";
import { defaultAllCategory } from "../pages/Templates/CategoryCode";
import * as Assets from "../components/Icon/assets";
import { Typography } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const RECOMMENDED_CATEGORY_ID = "6458f16991b315a609781eb3";

const cookies = new Cookies();
const getCookie = (item: string) => {
  return cookies.get(item);
};

// 조회 startDate를 결정한다. 하한은 항상 "오늘 0시"다:
// - 마지막 방문이 오늘이면(같은 날 재방문) 오늘 0시로 내려, 떠났다 곧 돌아와도
//   목록이 비지 않게 한다. (markVisited가 떠날 때 boundary를 "지금"으로 올리므로,
//   이 하한이 없으면 재방문 시 [방금~지금] 윈도우가 비어버린다.)
// - 마지막 방문이 오늘 0시 이전(며칠 전)이면 그 시점을 시작점으로 써 "지난 방문
//   이후" 의도를 유지한다. => startDate = min(lastVisited, 오늘0시)
const resolveStartDate = (last?: string): Date => {
  const startOfToday = dayjs().startOf("day");
  return last && dayjs(last).isBefore(startOfToday)
    ? dayjs(last).toDate()
    : startOfToday.toDate();
};

class AIReviewStore {
  constructor() {
    makeObservable(this, {
      application: observable,
      status: observable,
      startDate: observable,
      endDate: observable,
      gridOption: observable,
      headerStatus: observable,
      language: observable,
      category: observable,
      badge: observable,
      isBlind: observable,
      sortKey: observable,
      lastVisitedAt: observable,
    });
  }

  application = getCookie("ai_application") ? getCookie("ai_application") : "all";
  status = getCookie("ai_status") ? getCookie("ai_status") : "All";

  nowDate = dayjs().toDate();
  startDate: Date = resolveStartDate(getCookie("ai_lastVisitedAt"));

  endDate: Date = dayjs().toDate();

  headerStatus = localStorage.getItem("ai_columns") || "";
  language = getCookie("ai_language") ? getCookie("ai_language") : defaultAllCode;
  category = getCookie("ai_category")
    ? getCookie("ai_category")
    : defaultAllCategory;

  badge = "none";
  isBlind = false;

  sortKey = getCookie("ai_sortKey") ? getCookie("ai_sortKey") : "createdAt";

  lastVisitedAt: string = getCookie("ai_lastVisitedAt") ?? "";

  gridOption: DataGridProps = {
    columns: [
      {
        field: "index",
        headerName: "No",
        filterable: false,
        renderCell: (index) =>
          index.api.getRowIndex(index.row.id) +
          1 +
          this.gridOption.page * this.gridOption.rowsPerPage,
        width: 50,
        align: "center",
        hide: this.headerStatus.includes("index"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "thumbnail",
        headerName: "Thumbnail",
        width: 120,
        renderCell: (param) => (
          <div
            style={{ height: "100%", display: "flex", alignItems: "center" }}
          >
            <img
              style={{ height: "calc(100% - 5px)", maxWidth: "100px" }}
              src={param.row.thumbnail}
              alt=""
            />
          </div>
        ),
        hide: this.headerStatus.includes("thumbnail"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "modResult",
        headerName: "Mod",
        width: 110,
        sortable: false,
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const modResult = param.row.aiReview?.moderation?.result;
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color:
                  modResult === "Published"
                    ? "#1976D2"
                    : modResult === "Rejected"
                    ? "#B3261E"
                    : "inherit",
              }}
            >
              {modResult ?? "N/A"}
            </Typography>
          );
        },
      },
      {
        field: "minAge",
        headerName: "Age",
        width: 70,
        sortable: false,
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const minAge = param.row.aiReview?.ageRating?.minAge;
          return (
            <Typography variant="body2">
              {minAge !== undefined ? `${minAge}+` : "N/A"}
            </Typography>
          );
        },
      },
      {
        field: "totalScore",
        headerName: "Total",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.totalScore;
          return (
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {v != null ? v : "N/A"}
            </Typography>
          );
        },
        renderHeader: (params) => this.renderSortHeader(params, "totalScore"),
      },
      {
        field: "videoEditingScore",
        headerName: "Editing",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.videoEditingScore;
          return <Typography variant="body2">{v != null ? v : "N/A"}</Typography>;
        },
        renderHeader: (params) => this.renderSortHeader(params, "videoEditingScore"),
      },
      {
        field: "videoQualityScore",
        headerName: "Quality",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.videoQualityScore;
          return <Typography variant="body2">{v != null ? v : "N/A"}</Typography>;
        },
        renderHeader: (params) => this.renderSortHeader(params, "videoQualityScore"),
      },
      {
        field: "userId",
        headerName: "User ID",
        width: 120,
        hide: this.headerStatus.includes("userId"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "lang",
        headerName: "Lang",
        width: 75,
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "region",
        headerName: "Region",
        width: 110,
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "reviewStatus",
        headerName: "Status",
        width: 140,
        hide: this.headerStatus.includes("reviewer"),
        headerClassName: "ai-ungrouped-header",
        renderCell: (param) => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {param.row.isBlind ? (
                <Typography style={{ display: "flex", alignItems: "center" }}>
                  {param.row.reviewStatus}
                  <VisibilityOffIcon
                    style={{
                      marginLeft: "5px",
                      marginTop: "2px",
                      color: "#79747e",
                    }}
                  />
                </Typography>
              ) : (
                <Typography>{param.row.reviewStatus}</Typography>
              )}
            </div>
          );
        },
      },
      {
        field: "categories",
        headerName: "Category",
        width: 130,
        sortable: false,
        hide: this.headerStatus.includes("categories"),
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const recommended = param.row.categories?.find(
            (c: any) => c.id === RECOMMENDED_CATEGORY_ID
          );
          if (!recommended) return "";
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Typography variant="body2">Recommended</Typography>
              {recommended.pinned && (
                <PushPinIcon fontSize="small" sx={{ color: "#1976D2" }} />
              )}
            </div>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded Date",
        width: 240,
        hide: this.headerStatus.includes("createdAt"),
        headerClassName: "ai-ungrouped-header",
        renderHeader: (params) => this.renderSortHeader(params, "createdAt"),
      },
      {
        field: "link",
        headerName: "Link",
        width: 80,
        hide: this.headerStatus.includes("link"),
        headerClassName: "ai-ungrouped-header",
        renderCell: () => {
          return (
            <>
              <img
                src={Assets.NewWindows}
                alt="new window"
                style={{
                  width: 20,
                  height: 20,
                  cursor: "pointer",
                }}
              />
            </>
          );
        },
      },
    ],

    rows: [],
    totalCount: 0,
    loading: true,
    // AI Review는 last-visited로 진입마다 조회 범위가 바뀌어 이전 페이지 위치 복원이
    // 무의미하다. ai_page 쿠키를 신뢰하면 데이터가 줄었을 때 그 값이 범위를 벗어나
    // mount 후 DataGrid가 page를 자동 보정 → useDidMountEffect가 재발동해 setRows가
    // 두 번 호출된다. 항상 0으로 시작해 이 보정 자체를 없앤다.
    page: 0,
    rowsPerPage: Number(getCookie("ai_rowsPerPage"))
      ? Number(getCookie("ai_rowsPerPage"))
      : 100,
    sort: getCookie("ai_tSort") ? getCookie("ai_tSort") : "desc",
    columnGroupingModel: [
      {
        groupId: "score",
        headerName: "Score",
        children: [
          { field: "totalScore" },
          { field: "videoEditingScore" },
          { field: "videoQualityScore" },
        ],
      },
    ],
  };

  setApplication = action((application: string) => {
    this.application = application;
  });

  setStatus = action((status: string) => {
    this.status = status;
  });

  setStartDate = action((startDate: Date) => {
    this.startDate = startDate;
  });

  setEndDate = action((endDate: Date) => {
    this.endDate = endDate;
  });

  setGridOption = action((gridOption: any) => {
    this.gridOption = {
      ...this.gridOption,
      ...gridOption,
    };
  });
  setLanguageChange = action((value: any) => {
    this.language = value;
  });

  setCategoryChange = action((category: any) => {
    this.category = category;
  });

  setBadge = action((badge: any) => {
    this.badge = badge;
  });

  setIsBlind = action((blind: boolean) => {
    this.isBlind = blind;
  });

  setSortKey = action((value: string) => {
    this.sortKey = value;
  });

  renderSortHeader = (params: any, key: string) => {
    const active = this.sortKey === key;
    return (
      <div
        style={{
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        {params.colDef.headerName}
        {active && this.gridOption.sort === "desc" ? (
          <ArrowUpwardIcon fontSize="small" style={{ marginLeft: "5px" }} />
        ) : active && this.gridOption.sort === "asc" ? (
          <ArrowDownwardIcon fontSize="small" style={{ marginLeft: "5px" }} />
        ) : (
          <UnfoldMoreIcon
            fontSize="small"
            style={{ marginLeft: "5px", opacity: 0.35 }}
          />
        )}
      </div>
    );
  };

  onPageSizeChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.rowsPerPage = params;
  });

  onPageChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.page = params;
  });

  get getGridStatus() {
    return {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: this.startDate,
      endedAt: this.endDate,
      reviewStatus: this.status !== "All" ? this.status : undefined,
    };
  }

  setRows = action(async () => {
    this.setIsBlind(this.status === "Blind" ? true : false);

    const startedAtIso = dayjs(this.startDate)
      .add(1, "millisecond")
      .toISOString();
    const endedAtIso = dayjs(this.endDate).toISOString();

    const requestParameter = {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: startedAtIso,
      endedAt: endedAtIso,
      reviewStatus:
        this.status !== "All"
          ? this.status === "Blind"
            ? undefined
            : this.status
          : undefined,
      sort: this.gridOption.sort,
      language: this.language.value,
      category: this.category.value,
      application: this.application !== "all" ? this.application : "",
      isBlind: this.isBlind,
      sortKey: this.sortKey,
    };
    this.gridOption.loading = true;
    this.gridOption.rows = [];

    const blindParameter = {
      page: 1,
      display: 100,
      startedAt: startedAtIso,
      endedAt: endedAtIso,
      reviewStatus: undefined,
      sort: this.gridOption.sort,
      language: this.language.value,
      category: this.category.value,
      application: this.application !== "all" ? this.application : "",
      isBlind: true,
      sortKey: this.sortKey,
    };

    const allList: any[] = [
      getTemplates(requestParameter),
      getTemplates(blindParameter),
    ];

    await Promise.all(allList)
      .then((item) => {
        const templateMap = new Map();
        const blindTemplateIds = new Set();
        const resultList: any[] = [];

        item[0].templates.forEach((template: any) => {
          templateMap.set(template.templateId, {
            ...template,
            isBlind: false,
          });
        });

        item[1].templates.forEach((template: any) => {
          blindTemplateIds.add(template.templateId);
        });

        templateMap.forEach((template) => {
          const isBlind = blindTemplateIds.has(template.templateId);
          resultList.push({
            ...template,
            isBlind,
          });
        });

        const mapped = resultList.map((info: any) => {
          const reviewer = info.reviewer.split("@");
          return {
            ...info,
            id: info.templateId,
            thumbnail: info.imagePath,
            description: info.translations[0].description,
            createdAt: UtcDateFormatter(info.createdAt),
            userId: info.author.sub,
            name: info.author.name,
            lang: info.metaLanguageCode,
            region: `${
              info.metaCountryCode.length >= 249
                ? "All Country"
                : info.metaCountryCode
            }`,
            reviewStatus:
              info.reviewStatus === "InReview"
                ? "In Review"
                : info.reviewStatus,
            reviewer: reviewer[0],
          };
        });

        this.setGridOption({
          totalCount: item[0].totalCount,
          rows: mapped,
        });

        // ai_lastVisitedAt은 여기(매 조회)서 갱신하지 않는다. 조회마다 "지금"으로
        // 덮어쓰면 startDate(=직전 조회 시각)가 계속 앞당겨져, 새로고침 한 번에
        // 조회 window가 수초로 붕괴해 리스트가 사라진다. 갱신은 페이지를 떠날 때
        // (AIReview 언마운트) markVisited로 한 번만 한다.
      })
      .catch((err) => {
        console.log("err", err);
        this.gridOption.totalCount = 0;
      })
      .finally(() => {
        this.gridOption.loading = false;
      });
  });

  resetToLastVisited = action(() => {
    const last = getCookie("ai_lastVisitedAt");
    this.lastVisitedAt = last ?? "";
    // startDate 하한을 "오늘 0시"로 고정(resolveStartDate). 같은 날 재방문엔 오늘
    // 업로드분이 항상 보여 목록이 비지 않고, 며칠 전 방문이면 그 시점부터 보인다.
    // (하한이 없으면 markVisited가 떠날 때 올린 boundary 탓에 재방문 시 윈도우가
    // 수초로 좁혀져 비어버린다. 또 last 없을 때 "지금"으로 두면 startedAt+1ms가
    // endedAt을 넘겨 역전되는 문제도 함께 막는다.)
    this.startDate = resolveStartDate(last);
    this.endDate = dayjs().toDate();
  });

  // 페이지 진입 시각을 "마지막으로 본 시점"으로 기록한다. AIReview 언마운트
  // (다른 탭으로 SPA 이동 등) 시 한 번만 호출한다. 새로고침은 React cleanup을
  // 실행하지 않으므로 쿠키가 유지되어 리스트가 보존된다. 진입 시각을 쓰므로
  // 방문 중 올라온 업로드는 다음 진입에서 다시 보여 누락이 없다(약간의 중복 허용).
  markVisited = action((isoTime: string) => {
    const expires = dayjs().add(100, "year").toDate();
    cookies.set("ai_lastVisitedAt", isoTime, { path: "/", expires });
    this.lastVisitedAt = isoTime;
  });

  searchHandler = action(async () => {
    this.gridOption.page = 0;
    await this.setRows();
  });
}

export default AIReviewStore;
